/**
 * MeleeSwingRenderer — draws one melee swing (lane AZ, plan 23 row 50).
 *
 * Two looks, both anchored on the attacker's BODY CENTRE and sized from the
 * swing's real reach (the melee cone's `rangePx`; the smear is drawn at
 * `visualReachScale` of it — lane BB):
 *
 *  - smear (default): a tapered crescent whose outer edge is the reach arc,
 *    plus a bright leading-edge stroke. It rotates into place and fades.
 *  - lightning (Lightning Touch): a jagged bolt from the hand to the target
 *    (or to the reach point when there is none), two alternate bolts for a
 *    crackle, and a spark burst at the contact point.
 *  - the kits' authored styles (rend, guillotine, cleave, ram, hammer,
 *    crusade, shield_bash — plan 29 F9, lane BX) live in styledSwing.ts.
 *
 * Pixi 8 rule (docs/guides/PERF_ANALYSIS.md): every shape is drawn ONCE at
 * spawn; the animation is setRotation/setScale/setAlpha only — no per-frame
 * redraw, no `visible` flips. Reduced motion drops the sweep and the crackle
 * (one bolt, a plain fade).
 */
import { createGraphics } from '../../rendering/utils/Graphics';
import type { IGraphics } from '../../rendering/IRenderer';
import { MELEE_SWING_CONFIG, LIGHTNING_TOUCH_CONFIG, type MeleeSwingStyle } from '../../../config/abilities/meleeSwing.config';
import {
  crescentPoints, crescentArcAngles, wedgePoints, smearDrawRadius, boltPoints, sparkRays, seededRandom, easeOutCubic, lighten, type Point,
} from './meleeSwingShape';
import { coneOutlinePoints, type ConeSpec } from '../../../shared/logic/MeleeCone';
import { abilityAreaColor } from '../area/areaMarker';
import { drawStyledSwing, isStyledSwing } from './styledSwing';

export interface MeleeSwingDraw {
  /** Body centre of the attacker (world px). */
  x: number;
  y: number;
  isRight: boolean;
  color: number;
  /** Outer radius (px) — the swing's reach. */
  arcRadius: number;
  arcAngle: number;
  arcSpread: number;
  duration: number;
  style?: MeleeSwingStyle;
  /** Contact point for the lightning look (world px); null = reach point ahead. */
  contact?: Point | null;
  reducedMotion?: boolean;
  /** Seed for the bolt jitter (tests); defaults to Math.random. */
  seed?: number;
  /**
   * Plan 29 D55 — the swing's REAL hit area (`resolveMeleeCone`: full reach,
   * the flat swing plus its forgiveness margin). Drawn as a faint wash under
   * the crescent from the same `coneOutlinePoints` the hit test is checked
   * against, so the area shown is the area that lands.
   */
  cone?: ConeSpec;
}

/** The real-area wash polygon, around the swing's local origin (body centre). */
export function meleeRealAreaPoints(cone: ConeSpec, facingLeft: boolean): Point[] {
  return coneOutlinePoints({ x: 0, y: 0 }, facingLeft ? 'left' : 'right', cone, 24);
}

type Frame = (t: number) => void;

/** Drive `frame(t)` for t ∈ (0, 1] over `durationMs`, then `done()`. */
function animate(durationMs: number, frame: Frame, done: () => void): void {
  const start = performance.now();
  const tick = () => {
    const t = Math.min((performance.now() - start) / Math.max(1, durationMs), 1);
    frame(t);
    if (t < 1) requestAnimationFrame(tick);
    else done();
  };
  requestAnimationFrame(tick);
}

export function drawMeleeSwing(scene: unknown, d: MeleeSwingDraw): IGraphics[] {
  if (d.style === 'lightning') return drawLightning(scene, d);
  // Plan 29 F9 (lane BX): the warrior and paladin kits' authored swings.
  if (isStyledSwing(d.style)) return drawStyledSwing(scene, d, d.style);
  return drawSmear(scene, d);
}

function drawSmear(scene: unknown, d: MeleeSwingDraw): IGraphics[] {
  const cfg = MELEE_SWING_CONFIG;
  const facingLeft = !d.isRight;
  // Lane BB: drawn shorter than the real reach (DJ: "too big"); the cone
  // still hits out to d.arcRadius and the impact plays at the target.
  const radius = smearDrawRadius(d.arcRadius, cfg.visualReachScale, cfg.visualReachMinPx);
  const spec = {
    reach: radius, arcAngleDeg: d.arcAngle, arcSpreadDeg: d.arcSpread,
    thicknessFraction: cfg.smearThickness, facingLeft,
  };
  // Plan 29 D55: the faint wash over the whole real hit area, under the
  // crescent. It does not rotate in (the area is where the hit lands from the
  // first frame); it only fades with the swing.
  const wash = d.cone ? createGraphics(scene, { depth: cfg.depth - 1 }) : null;
  if (wash && d.cone) {
    wash.setBlendMode('add');
    wash.fillPolygon(meleeRealAreaPoints(d.cone, facingLeft), d.color, cfg.realAreaAlpha);
    wash.setPosition(d.x, d.y);
  }
  const g = createGraphics(scene, { depth: cfg.depth });
  g.setBlendMode('add');
  g.fillPolygon(wedgePoints(spec, cfg.wedgeInnerPx), d.color, cfg.wedgeAlpha);
  g.fillPolygon(crescentPoints(spec), d.color, cfg.smearAlpha);
  const arc = crescentArcAngles(spec);
  g.strokeArc(0, 0, radius, arc.start, arc.end, lighten(d.color, cfg.edgeLighten), cfg.edgeWidth, cfg.alpha);
  g.setPosition(d.x, d.y);

  // Rotate INTO place (the blade travels), easing out; fade over the back half.
  const lead = (facingLeft ? 1 : -1) * cfg.sweepLeadDeg * (Math.PI / 180);
  const sweep = !d.reducedMotion;
  const frame: Frame = (t) => {
    const e = easeOutCubic(t / 0.6);
    if (sweep) {
      g.setRotation(lead * (1 - e));
      g.setScale(cfg.startScale + (1 - cfg.startScale) * e);
    }
    const fade = t < 0.4 ? 1 : 1 - (t - 0.4) / 0.6;
    g.setAlpha(fade);
    wash?.setAlpha(fade);
  };
  frame(0);
  const all = wash ? [wash, g] : [g];
  animate(d.duration, frame, () => all.forEach((x) => x.destroy()));
  return all;
}

function drawLightning(scene: unknown, d: MeleeSwingDraw): IGraphics[] {
  const cfg = LIGHTNING_TOUCH_CONFIG;
  const rand = seededRandom(d.seed ?? Math.floor(Math.random() * 2 ** 31));
  const dir = d.isRight ? 1 : -1;
  // Hand = the leading edge of the 40 px body, slightly above centre.
  const hand: Point = { x: d.x + dir * 18, y: d.y - 4 };
  const contact: Point = d.contact ?? { x: d.x + dir * d.arcRadius * 0.85, y: d.y - 6 };
  // Bolts draw around the hand (local origin) — Lane-4 invariant.
  const local = { x: contact.x - hand.x, y: contact.y - hand.y };
  const color = d.color;
  const core = lighten(color, 0.7);

  const crackle = !d.reducedMotion;
  const paths = [boltPoints({ x: 0, y: 0 }, local, cfg.segments, cfg.jitterPx, rand)];
  if (crackle) paths.push(boltPoints({ x: 0, y: 0 }, local, cfg.segments, cfg.jitterPx, rand));

  // One additive graphic per path (halo + coloured stroke + core) so the
  // crackle can alternate them by alpha. Kept off the blurred glow layer:
  // its filter renders the layer's bounds into a pooled texture, which a
  // long bolt can enlarge — a cost the perf budget does not need.
  const bolts = paths.map((pts) => {
    const g = createGraphics(scene, { depth: MELEE_SWING_CONFIG.depth + 1 });
    g.setBlendMode('add');
    g.strokePolyline(pts, color, cfg.haloWidth, cfg.haloAlpha);
    g.strokePolyline(pts, color, cfg.glowWidth, cfg.glowAlpha);
    g.strokePolyline(pts, core, cfg.coreWidth, cfg.coreAlpha);
    g.setPosition(hand.x, hand.y);
    return g;
  });
  const [boltA, boltB = null] = bolts;

  const sparks = createGraphics(scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
  sparks.setBlendMode('add');
  for (const [a, b] of sparkRays(cfg.sparkCount, cfg.sparkInner, cfg.sparkOuter, rand)) {
    sparks.strokeLine(a.x, a.y, b.x, b.y, core, cfg.sparkWidth, 1);
  }
  sparks.fillCircle(0, 0, cfg.sparkInner + 2, core, 0.9);
  sparks.setPosition(contact.x, contact.y);

  const flips = cfg.crackleFlips;
  const frame: Frame = (t) => {
    const fade = t < 0.35 ? 1 : 1 - (t - 0.35) / 0.65;
    if (boltB) {
      // Alpha-only alternation: one bolt lit, the other dim — a crackle.
      const lit = Math.floor(t * flips) % 2 === 0;
      boltA.setAlpha(fade * (lit ? 1 : 0.25));
      boltB.setAlpha(fade * (lit ? 0.25 : 1));
    } else {
      boltA.setAlpha(fade);
    }
    const s = cfg.sparkStartScale + (1 - cfg.sparkStartScale) * easeOutCubic(t / 0.5);
    sparks.setScale(crackle ? s : 1);
    sparks.setAlpha(t < 0.2 ? 1 : 1 - (t - 0.2) / 0.8);
  };
  frame(0);
  const all = [...bolts, sparks];
  animate(cfg.durationMs, frame, () => all.forEach((g) => g.destroy()));
  return all;
}

/**
 * Plan 29 (lane BS): a cone ability's real hit wedge (Howl of Terror) — the
 * same `coneOutlinePoints` the hit test is checked against, apex on the body
 * centre, in the ability's area colour. Drawn once; fades by alpha only.
 */
export function drawAreaCone(scene: unknown, d: {
  x: number; y: number; facing: 'left' | 'right'; rangePx: number; arcDeg: number; abilityId?: string;
}): IGraphics {
  const color = (d.abilityId && abilityAreaColor(d.abilityId)) || MELEE_SWING_CONFIG.fallbackColor;
  const g = createGraphics(scene, { depth: MELEE_SWING_CONFIG.depth - 1 });
  const pts = coneOutlinePoints({ x: 0, y: 0 }, d.facing, { rangePx: d.rangePx, arcDeg: d.arcDeg }, 24);
  g.fillPolygon(pts, color, 0.16);
  g.strokePolyline([...pts, pts[0]], color, 2, 0.6);
  g.setPosition(d.x, d.y);
  animate(450, (t) => g.setAlpha(t < 0.3 ? 1 : 1 - (t - 0.3) / 0.7), () => g.destroy());
  return g;
}
